'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { Globe, MessageCircle } from 'lucide-react';
import { usePathname } from 'next/navigation';

export default function Footer() {
    const pathname = usePathname();
    const [year, setYear] = useState<number>(2026);

    useEffect(() => {
        setYear(new Date().getFullYear());
    }, []);

    const is404 = pathname === '/404' || pathname === '/not-found';

    if (is404) return null;

    return (
        <footer className="mt-20 w-full relative overflow-hidden bg-slate-900">
            {/* Top gradient border */}
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-green-500/40 to-transparent" />

            {/* Decorative Background Glows */}
            <div className="glow-orb -top-32 left-[15%] w-80 h-80 bg-green-500/[0.04]" />
            <div className="glow-orb -bottom-32 right-[15%] w-80 h-80 bg-emerald-500/[0.04]" style={{ animationDelay: '3s' }} />

            <div className="max-w-7xl mx-auto px-6 py-16 relative z-10">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-12 items-start">

                    {/* Brand Section */}
                    <div className="flex flex-col items-center md:items-start">
                        <Link href="/" className="group flex items-center gap-2.5 mb-4">
                            <span className="text-xl font-extrabold tracking-tight text-white group-hover:text-green-400 transition-colors">
                                BanglaVerse<span className="text-green-500"> Cloud</span>
                            </span>
                        </Link>
                        <p className="text-sm text-slate-400 max-w-[280px] text-center md:text-left leading-relaxed">
                            Next-generation Minecraft hosting powered by premium hardware and BDIX connectivity.
                        </p>
                    </div>

                    {/* Navigation Links */}
                    <div className="flex flex-col items-center">
                        <p className="text-[11px] font-bold text-slate-400 uppercase tracking-[0.15em] mb-4">Quick Links</p>
                        <div className="flex flex-wrap justify-center gap-2">
                            <FooterLink href="/tos">Terms</FooterLink>
                            <FooterLink href="/privacy">Privacy</FooterLink>
                            <FooterLink href="https://discord.gg/YX7E7xcXPN" target="_blank" rel="noopener noreferrer">Support</FooterLink>
                            <FooterLink href="/spark-analyzer" title="Minecraft Server Optimization Tool">Spark Analyzer</FooterLink>
                            <FooterLink href="/server-status" title="Minecraft Server Status & Ping Checker">Server Status</FooterLink>
                        </div>
                    </div>

                    {/* Social Buttons */}
                    <div className="flex flex-col items-center md:items-end">
                        <p className="text-[11px] font-bold text-slate-400 uppercase tracking-[0.15em] mb-4">Connect</p>
                        <div className="flex items-center gap-3">
                            <SocialButton href="https://facebook.com/banglaverse.cloud" icon={<Globe size={18} />} label="Facebook" hoverColor="hover:bg-blue-500/10 hover:text-blue-400 hover:border-blue-500/20" />
                            <SocialButton href="https://discord.gg/YX7E7xcXPN" icon={<MessageCircle size={18} />} label="Discord" hoverColor="hover:bg-indigo-500/10 hover:text-indigo-400 hover:border-indigo-500/20" />
                        </div>
                    </div>
                </div>

                {/* Divider */}
                <div className="my-10 h-px w-full bg-gradient-to-r from-transparent via-slate-700/50 to-transparent" />

                {/* Bottom Bar */}
                <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-xs font-medium text-slate-400">
                    <p className="tracking-wide">© {year} BanglaVerse Cloud. All rights reserved.</p>
                    <div className="flex items-center gap-2">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                        </span>
                        <span className="text-slate-300">All Systems Operational</span>
                    </div>
                </div>
            </div>
        </footer>
    );
}

function FooterLink({ href, children, title, target, rel }: { href: string; children: React.ReactNode; title?: string; target?: string; rel?: string }) {
    return (
        <Link
            href={href}
            title={title}
            target={target}
            rel={rel}
            className="px-4 py-2 rounded-xl text-sm font-medium text-slate-400 border border-slate-700/50 hover:bg-white/5 hover:text-white hover:border-slate-600 transition-all duration-300"
        >
            {children}
        </Link>
    );
}

function SocialButton({ href, icon, label, hoverColor }: { href: string; icon: React.ReactNode; label: string; hoverColor: string }) {
    return (
        <Link
            href={href}
            target="_blank"
            aria-label={label}
            className={`p-3 rounded-xl bg-white/5 border border-white/5 text-slate-400 transition-all duration-300 hover:scale-110 ${hoverColor}`}
        >
            {icon}
        </Link>
    );
}
